import type {
  NodeKey,
  NodeSource,
  NodeUnit,
  ProvenanceCapture,
  SlotInputNode,
  SlotValuationInputsResponse,
  TypeCurveParameterStatus,
} from 'types/packages-api/slot-valuation-inputs';
import {
  formatNodeDisplay,
  formatNodeValue,
  isDeclineParameterSource,
  slotCategoryOf,
  UNIT_HEADER_SUFFIX,
} from './utils';

/**
 * ── THE ROW GATES (INS-13307) ────────────────────────────────────────────────────────────
 *
 * The rows a slot's water rule, category and volume route rule out, the block and row notes,
 * and the sentences they carry. Read by the page and the workbook through
 * notUsedRowsForSection, blockNotesForSection and rowNotesForSection, so the two agree.
 */

/**
 * What the gates below read beyond the nodes themselves (INS-13307). Every field is optional,
 * and an absent one makes no claim: the gate that needs it marks nothing.
 */
export interface RowGateContext {
  /** `header.provenanceCapture`, for the area marker's caption test. */
  capture?: ProvenanceCapture | null;
  /** `header.slot.forecastStatus`: 'type-curve' or 'pdp', the run's own volume route. */
  forecastStatus?: string | null;
  /** `header.slot.splitWellWaterOptions`. */
  splitWellWaterOptions?: string[] | null;
  /** `header.slot.splitWellWaterSources`. */
  splitWellWaterSources?: string[] | null;
  /**
   * `header.slot.capabilityNotPricedRows`: the PDP decline rows the run's calculator could not
   * price (a ramp-up, a daily q_i), with `capabilityNote` saying why.
   */
  capabilityNotPricedRows?: NodeKey[] | null;
  /**
   * `header.slot.capabilityStatus`: not_declared, the calculator did not price them (a not-used
   * marker); not_recorded, the run was priced before it recorded what its calculator could price, so
   * they may not have been (a row note).
   */
  capabilityStatus?: 'not_recorded' | 'not_declared' | null;
  /** `header.slot.capabilityNote`. */
  capabilityNote?: string | null;
  /** A node key to its served `note`. */
  noteFor?: (key: NodeKey) => string | undefined;
}

/**
 * The gates' context for one response, built once for the page and the workbook so a new header
 * field reaches both (the literal was built twice, field by field).
 *
 * @param {SlotValuationInputsResponse} response - The served payload.
 * @param {object} meta - A node key to its served definition; only `note` is read.
 * @return {RowGateContext} The context.
 */
export function rowGateContextFor(
  response: SlotValuationInputsResponse,
  meta: Partial<Record<NodeKey, { note?: string | null }>>
): RowGateContext {
  const slot = response.header?.slot;
  return {
    capture: response.header?.provenanceCapture ?? null,
    forecastStatus: slot?.forecastStatus,
    splitWellWaterOptions: slot?.splitWellWaterOptions,
    splitWellWaterSources: slot?.splitWellWaterSources,
    capabilityNotPricedRows: slot?.capabilityNotPricedRows,
    capabilityStatus: slot?.capabilityStatus,
    capabilityNote: slot?.capabilityNote,
    noteFor: (key: NodeKey) => meta[key]?.note ?? undefined,
  };
}

/**
 * The PDP decline rows the run's calculator could not price, each to the server's sentence
 * (`capabilityNote`), for one `capabilityStatus` (EvaluationCalculation::CalculatorCapabilities):
 * not_declared rows are marked not used ({@link notUsedRowsForSection}); not_recorded rows carry it
 * as a row note ({@link rowNotesForSection}), since the run may have priced them.
 *
 * @param {RowGateContext} context - The header facts.
 * @param {string} status - The status the caller states.
 * @return {Map<NodeKey, string>} The rows, each to its sentence; none for another status.
 */
export function capabilityRows(
  context: RowGateContext,
  status: 'not_recorded' | 'not_declared'
): Map<NodeKey, string> {
  const note = context.capabilityNote;
  return new Map(
    note && context.capabilityStatus === status
      ? (context.capabilityNotPricedRows ?? []).map((key) => [key, note])
      : []
  );
}

/**
 * The Water row's note on a slot whose option is the DCA water forecast (INS-13307 AC 3). Only
 * there: a ratio or no-option slot behaves as it does today and carries none,
 * and a merged split well with a ratio unit already has the split-well note. Worded from the
 * option, not the config: `dca` is also the option of a slot no config covered.
 */
export const DCA_WATER_NOTE =
  "This slot's water forecast option is DCA, so it skips the ratio and keeps its forecast's own water.";

/**
 * The INS-13307 not-used sentences: our wording, pending Andrew's approval. The ticket's
 * wording for the first is "Not used: DCA water forecast".
 */
export const NOT_USED_DCA_WATER = 'Not used: water from the DCA forecast';
export const NOT_USED_RATIO_WATER = 'Not used: water from the ratio';

/**
 * The note on a decline rate whose stored unit the backend could not convert to the row's
 * (INS-13307; `unitNotRecognised`). The row shows no value.
 */
export const UNIT_NOT_RECOGNISED_NOTE = 'Unit not recognised';

export const NOT_USED_WATER_FROM_GAS =
  'Not used: water is derived from gas, by the water / gas ratio';
export const NOT_USED_WATER_FROM_OIL =
  'Not used: water is derived from oil, by the water cut';
export const NOT_USED_UNRANKED =
  "Not used: this slot's category is not ranked on it";
export const NOT_USED_OWN_DECLINE_LENGTH =
  "Not used: volumes from the well's own decline are not rescaled";
/**
 * The block note beside Leasehold Cashflow on a run that never wrote `water_forecast_option`.
 * The registry formula names the lease pair, which eval_calculator reads from 0.86.0 (#509,
 * 2026-09-15); a run that predates the option column may predate that too (ONCALL-888), and
 * the run records no version to say which. Every run since #519 writes the column, and #519
 * came after 0.86.0, so a run that carries it took the lease pair.
 */
export const LEASEHOLD_BEFORE_086_NOTE =
  'Runs priced before eval_calculator 0.86.0 (2026-09-15) took the leasehold burden from the holder Royalty % and ORRI %, not the lease royalty and lease ORRI.';
/**
 * The block note when the version's parameters were written after the run started
 * (`typeCurveParameterStatus` changed_since_run). The rows are that version as it reads now;
 * PromotedTypeCurveParameterReader cannot read back what the run sent, so the page says it may
 * differ rather than claim either.
 */
export const TYPE_CURVE_CHANGED_SINCE_RUN_NOTE =
  "This version's parameters changed after this run started; the run may have priced different values.";

/**
 * The row note on a split well's water rows when its units take different options.
 *
 * @param {string[]} options - The distinct options, as served.
 * @return {string} The note.
 */
export function splitWellWaterNote(options: string[]): string {
  return (
    `This well is split across units whose configs take different water options ` +
    `(${options.join(', ')}). Each unit's water was priced on its own option; these rows ` +
    "show the largest-gas unit's."
  );
}

/**
 * The row note on a split well's ratio rows when its ratio units take different water sources.
 *
 * @param {string[]} sources - The distinct sources, as served.
 * @return {string} The note.
 */
export function splitWellWaterSourcesNote(sources: string[]): string {
  return (
    `This well is split across units whose configs take different water sources ` +
    `(${sources.join(', ')}). Each unit's water was derived from its own source, so neither ` +
    "ratio input is marked not used; these rows show the largest-gas unit's."
  );
}

/**
 * The run's value of one node, or undefined when it resolved nothing. The run's own value
 * where a pending overlay displaced it — see rowsNotUsedByRun for why.
 *
 * @param {SlotInputNode[]} slotInputs - Every node in the payload.
 * @param {NodeKey} key - The node.
 * @return {unknown} Its value.
 */
function runValueOf(slotInputs: SlotInputNode[], key: NodeKey): unknown {
  const node = slotInputs.find((candidate) => candidate.key === key);
  if (!node?.applied) {
    return undefined;
  }
  const entry = node.values?.[0];
  return entry?.runValue ?? entry?.value;
}

/**
 * The water forecast option the calculator applied to this slot, lowercased, or null when the
 * run recorded none. `dca` includes a slot no config covered (eval_calculator forces it).
 *
 * @param {SlotInputNode[]} slotInputs - Every node in the payload.
 * @return {(string|null)} 'dca', 'ratio', or null.
 */
export function waterForecastOption(
  slotInputs: SlotInputNode[]
): string | null {
  const value = runValueOf(slotInputs, 'water_forecast_option');
  return typeof value === 'string' && value.length > 0
    ? value.toLowerCase()
    : null;
}

/**
 * Whether the run wrote `water_forecast_option` at all (INS-13307). `absent` is a run that
 * never wrote the column (before eval_calculator #519), and the page makes NO claim about its
 * water inputs: such a run spans the INS-10669 change to type-curve water and 0.86.0's change
 * to the leasehold burden, and records which side of either it is on nowhere.
 *
 * @param {SlotInputNode[]} slotInputs - Every node in the payload.
 * @return {('applied'|'recordedNull'|'absent')} The option's state.
 */
export function waterForecastOptionState(
  slotInputs: SlotInputNode[]
): 'applied' | 'recordedNull' | 'absent' {
  const node = slotInputs.find(
    (candidate) => candidate.key === 'water_forecast_option'
  );
  if (node?.applied) {
    return 'applied';
  }
  return node?.recordedNull ? 'recordedNull' : 'absent';
}

/**
 * The split well's options where the page states them (the split-well note), else none: two
 * or more options, on a slot the calculator merged (fractionalized_months > 0).
 *
 * @param {SlotInputNode[]} slotInputs - Every node in the payload.
 * @param {RowGateContext} context - The header facts.
 * @return {string[]} The options, or an empty list.
 */
function splitWellOptionsShown(
  slotInputs: SlotInputNode[],
  context: RowGateContext
): string[] {
  return mergedSplit(slotInputs, context.splitWellWaterOptions);
}

/**
 * The split well's ratio sources where they differ, on a slot the calculator merged
 * (fractionalized_months > 0), else none.
 *
 * @param {SlotInputNode[]} slotInputs - Every node in the payload.
 * @param {RowGateContext} context - The header facts.
 * @return {string[]} The sources, or an empty list.
 */
function splitWellSourcesShown(
  slotInputs: SlotInputNode[],
  context: RowGateContext
): string[] {
  return mergedSplit(slotInputs, context.splitWellWaterSources);
}

/**
 * A split well's differing values, two or more, on a slot the calculator merged
 * (fractionalized_months > 0); else none.
 *
 * @param {SlotInputNode[]} slotInputs - Every node in the payload.
 * @param {(string[]|null)} [values] - The values across the well's units.
 * @return {string[]} The values, or an empty list.
 */
function mergedSplit(
  slotInputs: SlotInputNode[],
  values: string[] | null | undefined
): string[] {
  const split = values ?? [];
  const months = runValueOf(slotInputs, 'fractionalized_months');
  return split.length > 1 && typeof months === 'number' && months > 0
    ? split
    : [];
}

/**
 * The ratio inputs and source the Water function reads — `water_volumes`'s own declared
 * inputs that are water inputs, so a registry change moves this with it. Excludes the option,
 * which decides the branch and is never "not used".
 *
 * @param {SlotInputNode[]} slotInputs - Every node in the payload.
 * @return {NodeKey[]} water_cut_percentage, water_gas_ratio_in_bbl_per_mmcf,
 *   water_forecast_source.
 */
function waterRatioInputs(slotInputs: SlotInputNode[]): NodeKey[] {
  const water = slotInputs.find((node) => node.key === 'water_volumes');
  return (water?.inputs ?? []).filter(
    (key) => key.startsWith('water_') && key !== 'water_forecast_option'
  );
}

/**
 * Whether any RESOLVED node on the slot reads from one source vocabulary — how the gates
 * below tell a type curve, a DCA fit and a Legacy forecast apart, the same test
 * {@link volumeSourceKind} applies.
 *
 * @param {SlotInputNode[]} slotInputs - Every node in the payload.
 * @param {Function} sourceFor - A node key to its served registry source.
 * @param {string} vocabulary - The source's second element, e.g. 'dca_parameters'.
 * @return {boolean} True when one such node resolved.
 */
export function appliedFromVocabulary(
  slotInputs: SlotInputNode[],
  sourceFor: (key: NodeKey) => NodeSource | undefined,
  vocabulary: string
): boolean {
  return slotInputs.some((node) => {
    const source = sourceFor(node.key);
    return node.applied && Array.isArray(source) && source[1] === vocabulary;
  });
}

/**
 * Whether a served source is the WATER stream of a decline-parameter store: both stores key
 * their streams `water.<term>`. EUR is `top.water_eur` and is not a curve term.
 *
 * @param {NodeSource} [source] - The node's served `source`.
 * @return {boolean} True for a water decline-parameter row.
 */
function isWaterDeclineSource(source: NodeSource | undefined): boolean {
  if (!isDeclineParameterSource(source) || !Array.isArray(source)) {
    return false;
  }
  const path = String(source[2] ?? '');
  return path.startsWith('water.');
}

/**
 * ── THE WATER ROWS THIS RUN DID NOT USE (INS-13307) ──────────────────────────────────────
 *
 * Read off eval_calculator fill_forecast_water / net_volumes (vol_transformer.py:579-666):
 *
 *   dca    the slot keeps its forecast's own water, so the ratio inputs beside it — the
 *          Scenario's basin/tier set, which a DCA row carries for the export — were not used.
 *          A slot no config covers is `dca` too.
 *   ratio  forecast water is derived from oil or gas, so the water decline terms (type curve,
 *          DCA or Legacy) did not reach the result.
 *
 * And within the ratio, one source: water from gas never reads the water cut, water from oil
 * never reads the water / gas ratio.
 *
 * ⚠️ A SLOT WITH NO OPTION MARKS NOTHING (AC 3), whether the run never wrote the column or
 * recorded it as null: it shows as it did before the configs.
 *
 * ⚠️ A SPLIT WELL WHOSE UNITS TAKE BOTH OPTIONS keeps the other option's rows unmarked. The
 * rows show the largest-gas unit's option, but on `dca` a ratio unit's water read the water
 * cut / WGR and the source, and on `ratio` a dca unit's water read the water decline terms, so
 * "not used" would be false for that unit; the split-well note says how the well was priced.
 *
 * ⚠️ A SPLIT WELL WHOSE RATIO UNITS TAKE DIFFERENT SOURCES marks neither the water cut nor the
 * WGR: one unit derived its water from oil by the cut, the other from gas by the WGR, so each
 * input was used by one of them.
 *
 * @param {SlotInputNode[]} slotInputs - Every node in the payload.
 * @param {Function} sourceFor - A node key to its served registry source.
 * @param {RowGateContext} [context] - The header facts (the split well's options).
 * @return {Map<NodeKey, string>} Marked rows, each to its marker's explanation.
 */
export function waterRowsNotUsed(
  slotInputs: SlotInputNode[],
  sourceFor: (key: NodeKey) => NodeSource | undefined,
  context: RowGateContext = {}
): Map<NodeKey, string> {
  const marked = new Map<NodeKey, string>();
  const option = waterForecastOption(slotInputs);
  if (option === 'dca') {
    if (!splitWellOptionsShown(slotInputs, context).includes('ratio')) {
      waterRatioInputs(slotInputs).forEach((key) =>
        marked.set(key, NOT_USED_DCA_WATER)
      );
    }
    return marked;
  }
  if (option !== 'ratio') {
    return marked;
  }
  const splitIncludesDca = splitWellOptionsShown(slotInputs, context).includes(
    'dca'
  );
  if (!splitIncludesDca) {
    slotInputs.forEach((node) => {
      if (isWaterDeclineSource(sourceFor(node.key))) {
        marked.set(node.key, NOT_USED_RATIO_WATER);
      }
    });
  }
  if (splitWellSourcesShown(slotInputs, context).length > 0) {
    return marked;
  }
  const phase = runValueOf(slotInputs, 'water_forecast_source');
  if (phase === 'gas') {
    marked.set('water_cut_percentage', NOT_USED_WATER_FROM_GAS);
  } else if (phase === 'oil') {
    marked.set('water_gas_ratio_in_bbl_per_mmcf', NOT_USED_WATER_FROM_OIL);
  }
  return marked;
}

/**
 * ── THE RANK INPUTS ON A CATEGORY THAT IS NOT RANKED ON THEM (INS-13307) ─────────────────
 *
 * formation_depth and slot_number only order a pace train: LTDs on four keys, each Create
 * New Well status on three plus the uwi (slot_scheduler.rb:25-64). DUC, Permit and PDP are not
 * ranked at all. Derived from the registry: a key is a rank input when some `*_rank` node lists
 * it, and it is used here only when THIS category's rank node does.
 *
 * Fails open: an unresolved category marks nothing.
 *
 * @param {SlotInputNode[]} slotInputs - Every node in the payload.
 * @return {Set<NodeKey>} The rank inputs this slot's category does not rank on.
 */
export function rankInputsNotUsed(slotInputs: SlotInputNode[]): Set<NodeKey> {
  const category = slotCategoryOf(slotInputs);
  if (!category) {
    return new Set<NodeKey>();
  }
  const rankInputs = new Set<NodeKey>(
    slotInputs
      .filter((node) => node.key.endsWith('_rank'))
      .flatMap((node) => node.inputs ?? [])
  );
  const own = slotInputs.find((node) => node.key === `${category}_rank`);
  return new Set<NodeKey>(
    [...rankInputs].filter((key) => !(own?.inputs ?? []).includes(key))
  );
}

/**
 * The marker on a DCA forecast's exponential ramp-up rows (INS-13307 round 7, B5): eval_calculator
 * #527 values that stream as the unramped decline, with a warning, because the engine overstates an
 * exponential ramp's first month. A type curve's exponential ramp is priced (through /dca/calculate)
 * and is not marked.
 */
export const NOT_USED_EXPONENTIAL_RAMP =
  'Not used: an exponential ramp-up is valued as the unramped decline';

/** The ramp-up nodes of a DCA forecast stream. Keep in step with PdpDeclineReader::RAMP_UP_PARTS. */
const DCA_RAMP_UP_PARTS = ['model', 'qi', 'slope', 'months'] as const;

/**
 * The ramp-up rows of each DCA forecast stream whose ramp model is exponential: the calculator did
 * not value them ({@link NOT_USED_EXPONENTIAL_RAMP}).
 *
 * @param {SlotInputNode[]} slotInputs - Every node in the payload.
 * @return {Set<NodeKey>} The rows to mark.
 */
export function exponentialRampRowsNotUsed(
  slotInputs: SlotInputNode[]
): Set<NodeKey> {
  const marked = new Set<NodeKey>();
  ['oil', 'gas', 'water'].forEach((stream) => {
    if (
      runValueOf(slotInputs, `pdp_${stream}_ramp_up_model`) === 'exponential'
    ) {
      DCA_RAMP_UP_PARTS.forEach((part) =>
        marked.add(`pdp_${stream}_ramp_up_${part}`)
      );
    }
  });
  return marked;
}

/**
 * The lateral lengths on a slot whose volumes are its own decline (INS-13307). eval_calculator
 * rescales only `forecast_status == "type-curve"` volumes and gives every other slot a ratio of
 * 1.0 (load/slots.py:107-165), so the two lengths that make the ratio did not reach the result.
 * Derived from `volume_adjustment_ratio`'s declared inputs. No status, no claim.
 *
 * @param {SlotInputNode[]} slotInputs - Every node in the payload.
 * @param {(string|null)} [forecastStatus] - `header.slot.forecastStatus`.
 * @return {Set<NodeKey>} The unused length rows.
 */
export function lengthInputsNotUsed(
  slotInputs: SlotInputNode[],
  forecastStatus?: string | null
): Set<NodeKey> {
  if (!forecastStatus || forecastStatus === 'type-curve') {
    return new Set<NodeKey>();
  }
  const ratio = slotInputs.find(
    (node) => node.key === 'volume_adjustment_ratio'
  );
  return new Set<NodeKey>(ratio?.inputs ?? []);
}

/**
 * What the block notes read beyond the rows (INS-13307): the registry lookups, and the header's
 * type-curve parameter status. The status is optional, and absent is an older payload.
 */
export interface BlockNoteContext {
  /** A node key to its served registry source. */
  sourceFor: (key: NodeKey) => NodeSource | undefined;
  /** A node key to its served unit, for the Volume Adjustment Ratio note's figures. */
  unitFor?: (key: NodeKey) => NodeUnit | undefined;
  /** `header.slot.typeCurveParameterStatus`. */
  typeCurveParameterStatus?: TypeCurveParameterStatus | null;
}

/**
 * The block note for a section that renders type-curve parameter rows, or null. Read by the
 * page (a caption under the block's header) and the workbook (the block header's Source cell)
 * from the same rendered rows, so the two agree.
 *
 * By status (INS-13307):
 *
 *   changed_since_run    {@link TYPE_CURVE_CHANGED_SINCE_RUN_NOTE}, beside the priced version
 *   as_priced            none: the caption already says the rows are what the run priced
 *   pre_baked            none: no rows are shown, and the caption says why
 *   absent               none
 *
 * @param {SlotInputNode[]} rows - The block's rendered rows ({@link renderedRowNodes}).
 * @param {Function} sourceFor - A node key to its served registry source.
 * @param {(TypeCurveParameterStatus|null)} [status] - `header.slot.typeCurveParameterStatus`.
 * @return {(string|null)} The note, or null.
 */
export function typeCurveSectionNote(
  rows: SlotInputNode[],
  sourceFor: (key: NodeKey) => NodeSource | undefined,
  status?: TypeCurveParameterStatus | null
): string | null {
  if (status === 'changed_since_run') {
    return appliedFromVocabulary(rows, sourceFor, 'type_curve_dca_parameters')
      ? TYPE_CURVE_CHANGED_SINCE_RUN_NOTE
      : null;
  }
  return null;
}

/** The streams a type curve carries, in the order the rows list them. */
const TYPE_CURVE_STREAMS = ['oil', 'gas', 'water'] as const;

/**
 * ── THE VOLUME ADJUSTMENT RATIO, APPLIED TO THE CURVE ON SCREEN (INS-13307) ──────────────
 *
 * eval_calculator multiplies a type-curve slot's monthly volumes by the ratio of its lateral
 * length to the curve's (load/slots.py:107-165), so the q_i the rows show is not the rate this
 * slot was priced at. Misha read the curve's 14,953 against MSS_1's first month and could not
 * reconcile them; the factor was 1.292906. This states the product, per stream, on the block.
 *
 * The rates are the block's own rendered `tc_*` rows, read by key — the priced version — and
 * formatted as their rows are. The product is rounded
 * to a whole rate: eight decimals of a multiplied rate is precision nobody measured.
 *
 * Null off a type curve, without a resolved ratio, or at exactly 1 (nothing was rescaled).
 *
 * @param {SlotInputNode[]} rows - The block's rendered rows.
 * @param {SlotInputNode[]} allNodes - Every node in the payload, for the ratio.
 * @param {BlockNoteContext} context - The lookups and the status.
 * @return {(string|null)} The note, or null.
 */
export function volumeAdjustmentRatioNote(
  rows: SlotInputNode[],
  allNodes: SlotInputNode[],
  context: BlockNoteContext
): string | null {
  const { sourceFor, unitFor = () => undefined } = context;
  const priced = appliedFromVocabulary(
    rows,
    sourceFor,
    'type_curve_dca_parameters'
  );
  const ratio = runValueOf(allNodes, 'volume_adjustment_ratio');
  if (
    !priced ||
    typeof ratio !== 'number' ||
    !Number.isFinite(ratio) ||
    ratio === 1
  ) {
    return null;
  }
  const label =
    allNodes.find((node) => node.key === 'volume_adjustment_ratio')?.label ??
    'Volume Adjustment Ratio';
  const lead =
    `The run multiplies this curve's monthly volumes by the ${label} ` +
    `(${formatNodeValue(ratio, unitFor('volume_adjustment_ratio'))})`;
  const terms = scaledRateTerms(rows, ratio, unitFor);
  return terms.length > 0 ? `${lead}: ${terms.join('; ')}.` : `${lead}.`;
}

/**
 * Each stream's rendered q_i, and ramp-up starting rate where the priced version has one, with
 * its product by the ratio: "Oil q_i 14,953 → 19,333 BBL/mo".
 *
 * @param {SlotInputNode[]} rows - The block's rendered rows.
 * @param {number} ratio - The Volume Adjustment Ratio.
 * @param {Function} unitFor - A node key to its served unit.
 * @return {string[]} The terms, in stream order.
 */
function scaledRateTerms(
  rows: SlotInputNode[],
  ratio: number,
  unitFor: (key: NodeKey) => NodeUnit | undefined
): string[] {
  /**
   * A rendered row's resolved rate, or undefined when it has none.
   *
   * @param {NodeKey} key - The row.
   * @return {(number|undefined)} Its run value.
   */
  const rateOf = (key: NodeKey): number | undefined => {
    const value = runValueOf(rows, key);
    return typeof value === 'number' && Number.isFinite(value)
      ? value
      : undefined;
  };
  /**
   * One term: the rate exactly as its row shows it (formatNodeDisplay, so a ramp-up starting
   * rate reads at its row's 2 decimals), and its product by the ratio, to a whole rate.
   *
   * @param {string} name - "Oil q_i".
   * @param {NodeKey} key - The row, for its unit and display format.
   * @param {number} value - Its rate.
   * @return {string} "Oil q_i 14,953 → 19,333 BBL/mo".
   */
  const term = (name: string, key: NodeKey, value: number): string => {
    const unit = unitFor(key);
    const suffix = unit ? UNIT_HEADER_SUFFIX[unit] : null;
    const scaled = formatNodeValue(Math.round(value * ratio), unit);
    return `${name} ${formatNodeDisplay(key, value, unit)} → ${scaled}${suffix ? ` ${suffix}` : ''}`;
  };
  return TYPE_CURVE_STREAMS.flatMap((stream) => {
    const name = stream.charAt(0).toUpperCase() + stream.slice(1);
    const qiKey = `tc_${stream}_q_i`;
    const qi = rateOf(qiKey);
    const rampKey = `tc_${stream}_ramp_up_qi`;
    const ramp = rateOf(rampKey);
    return [
      ...(qi !== undefined ? [term(`${name} q_i`, qiKey, qi)] : []),
      ...(ramp !== undefined
        ? [term(`${name} ramp-up q_i`, rampKey, ramp)]
        : []),
    ];
  });
}

/**
 * The block note for the block that shows Leasehold Cashflow, on a run that never wrote
 * `water_forecast_option` ({@link LEASEHOLD_BEFORE_086_NOTE}), or null. A block note rather than
 * a row note because the node is a monthly column: it renders in the block's series disclosure,
 * with its formula in the column header, and has no row to carry a marker. Read by the page and
 * the workbook from the same rows and columns.
 *
 * @param {SlotInputNode[]} rows - The block's rendered rows ({@link renderedRowNodes}).
 * @param {NodeKey[]} seriesColumns - The block's monthly columns.
 * @param {SlotInputNode[]} allNodes - Every node in the payload, for the option.
 * @return {(string|null)} The note, or null.
 */
export function leaseholdCashflowSectionNote(
  rows: SlotInputNode[],
  seriesColumns: NodeKey[],
  allNodes: SlotInputNode[]
): string | null {
  const shown =
    seriesColumns.includes('leasehold_cashflow') ||
    rows.some((node) => node.key === 'leasehold_cashflow');
  return shown && waterForecastOptionState(allNodes) === 'absent'
    ? LEASEHOLD_BEFORE_086_NOTE
    : null;
}

/**
 * Every block note for one block, in the order the page renders its captions: the type-curve
 * note, the Volume Adjustment Ratio note, then the leasehold note. The page renders each as a
 * caption; the workbook joins them.
 *
 * @param {SlotInputNode[]} rows - The block's rendered rows.
 * @param {NodeKey[]} seriesColumns - The block's monthly columns.
 * @param {SlotInputNode[]} allNodes - Every node in the payload.
 * @param {BlockNoteContext} context - The registry lookups and the type-curve status.
 * @return {string[]} The notes, possibly none.
 */
export function blockNotesForSection(
  rows: SlotInputNode[],
  seriesColumns: NodeKey[],
  allNodes: SlotInputNode[],
  context: BlockNoteContext
): string[] {
  return [
    typeCurveSectionNote(
      rows,
      context.sourceFor,
      context.typeCurveParameterStatus
    ),
    volumeAdjustmentRatioNote(rows, allNodes, context),
    leaseholdCashflowSectionNote(rows, seriesColumns, allNodes),
  ].filter((note): note is string => note !== null);
}

/**
 * ── THE ROW NOTES: A FACT ABOUT HOW A VALUE WAS USED, SHORT OF "NOT USED" (INS-13307) ─────
 *
 * Six sources, joined per row:
 *
 *   unit           `unitNotRecognised` on the node: a decline rate whose stored unit the
 *                  backend could not convert to the row's, shown with no value
 *                  ({@link UNIT_NOT_RECOGNISED_NOTE})
 *   the registry   `note` on the node definition, true on every slot the row renders for
 *   split well     units with different options: each unit was priced on its own, and the
 *                  rows show the largest-gas unit's (fractionalization.py:146-177). Only where
 *                  the calculator merged the units (fractionalized_months > 0).
 *   split sources  ratio units with different water sources, on the ratio rows: each unit
 *                  derived its water from its own source, which is why neither ratio input is
 *                  marked. Merged units only, as above.
 *   capability     the PDP decline rows a run priced before it recorded its calculator's
 *                  capabilities may not have priced (`capabilityStatus` not_recorded)
 *   DCA water      the Water row on a slot whose option is dca ({@link DCA_WATER_NOTE}), unless
 *                  a merged split well has a ratio unit (the split-well note covers it)
 *
 * ⚠️ A ROW THE NOT-USED GATE MARKS CARRIES NO NOTE. The marker is the row's whole claim; a note
 * beside it either repeats it or says the value was used.
 *
 * Read by the page and the workbook, like {@link notUsedRowsForSection}.
 *
 * @param {SlotInputNode[]} slotInputs - Every node in the payload.
 * @param {RowGateContext} [context] - The header facts and served definitions.
 * @param {Map} [marked] - The block's not-used rows ({@link notUsedRowsForSection}).
 * @return {Map<NodeKey, string>} Rows with a note, each to its text.
 */
export function rowNotesForSection(
  slotInputs: SlotInputNode[],
  context: RowGateContext = {},
  marked: ReadonlyMap<NodeKey, string> = new Map()
): Map<NodeKey, string> {
  const notes = new Map<NodeKey, string>();
  /**
   * Adds a note to a row, after any it already carries.
   *
   * @param {NodeKey} key - The row.
   * @param {string} text - The note.
   */
  const add = (key: NodeKey, text: string) => {
    if (!marked.has(key)) {
      notes.set(key, notes.has(key) ? `${notes.get(key)} ${text}` : text);
    }
  };

  slotInputs
    .filter((node) => node.unitNotRecognised)
    .forEach((node) => add(node.key, UNIT_NOT_RECOGNISED_NOTE));

  if (context.noteFor) {
    slotInputs.forEach((node) => {
      const note = context.noteFor?.(node.key);
      if (note) {
        add(node.key, note);
      }
    });
  }

  const split = splitWellOptionsShown(slotInputs, context);
  if (split.length > 0) {
    const note = splitWellWaterNote(split);
    ['water_volumes', 'water_forecast_option', ...waterRatioInputs(slotInputs)]
      .filter((key) => slotInputs.some((node) => node.key === key))
      .forEach((key) => add(key, note));
  }

  capabilityRows(context, 'not_recorded').forEach((note, key) =>
    add(key, note)
  );

  if (
    waterForecastOption(slotInputs) === 'dca' &&
    !split.includes('ratio') &&
    slotInputs.some((node) => node.key === 'water_volumes')
  ) {
    add('water_volumes', DCA_WATER_NOTE);
  }

  const sources = splitWellSourcesShown(slotInputs, context);
  if (sources.length > 0) {
    const note = splitWellWaterSourcesNote(sources);
    waterRatioInputs(slotInputs)
      .filter((key) => slotInputs.some((node) => node.key === key))
      .forEach((key) => add(key, note));
  }
  return notes;
}
